import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from '@/types/chat';
import { RealtimeChannel } from '@supabase/supabase-js';
import { ChatMessageSchema, validateData } from '@/lib/validation/schemas';

export class ChatService {
  private static subscriptions: Map<string, RealtimeChannel> = new Map();

  // Get messages for a specific site office
  // Cache of sender info to avoid repeated lookups
  private static senderCache: Map<string, { name: string; imageUrl: string; isAdmin: boolean }> = new Map();

  static async getMessages(siteOfficeId: string, limit: number = 30): Promise<ChatMessage[]> {
    try {
      // Fetch messages WITHOUT joining team_members to avoid RLS overhead
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, text, timestamp, sender_id, site_office_id, is_read, type, archived')
        .eq('site_office_id', siteOfficeId)
        .eq('archived', false)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching messages:', error);
        return [];
      }

      // Collect unique sender IDs that aren't cached
      const senderIds = [...new Set((data || []).map(m => m.sender_id))];
      const uncachedIds = senderIds.filter(id => !this.senderCache.has(id));

      // Fetch uncached sender info using SECURITY DEFINER RPC (bypasses RLS)
      if (uncachedIds.length > 0) {
        const { data: members } = await supabase
          .rpc('get_team_members_for_display');
        
        if (members) {
          for (const m of members) {
            this.senderCache.set(m.member_id, {
              name: m.member_name,
              imageUrl: m.member_image_url || '',
              isAdmin: m.member_role === 'administrator',
            });
          }
        }
      }

      return (data || []).reverse().map((msg: any) => {
        const sender = this.senderCache.get(msg.sender_id);
        return {
          id: msg.id,
          text: msg.text,
          timestamp: new Date(msg.timestamp),
          senderId: msg.sender_id,
          senderName: sender?.name || 'Unknown',
          senderAvatar: sender?.imageUrl || '',
          siteOfficeId: msg.site_office_id,
          isRead: msg.is_read,
          isAdmin: sender?.isAdmin || false,
          type: msg.type || 'text',
        };
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  }

  // Send a new message
  static async sendMessage(
    text: string,
    senderId: string,
    siteOfficeId: string,
    type: 'text' | 'image' = 'text'
  ): Promise<boolean> {
    try {
      // Validate input data
      const validatedMessage = validateData(ChatMessageSchema, {
        text,
        sender_id: senderId,
        site_office_id: siteOfficeId,
        type,
      });
      
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          text: validatedMessage.text,
          sender_id: validatedMessage.sender_id,
          site_office_id: validatedMessage.site_office_id,
          type: validatedMessage.type,
        });

      if (error) {
        console.error('Error sending message:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  // Mark message as read
  static async markAsRead(messageId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) {
        console.error('Error marking message as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error marking message as read:', error);
      return false;
    }
  }

  // Subscribe to real-time message updates
  static subscribeToMessages(
    siteOfficeId: string,
    onMessage: (message: ChatMessage) => void,
    onError?: (error: any) => void
  ): () => void {
    try {
      const channelName = `chat-${siteOfficeId}`;
      
      // Unsubscribe from existing channel if any
      if (this.subscriptions.has(channelName)) {
        supabase.removeChannel(this.subscriptions.get(channelName)!);
      }

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `site_office_id=eq.${siteOfficeId}`,
          },
          async (payload) => {
            // Use cached sender info to avoid RLS-heavy team_members query
            let sender = this.senderCache.get(payload.new.sender_id);
            if (!sender) {
              // Refresh cache if sender not found
              const { data: members } = await supabase.rpc('get_team_members_for_display');
              if (members) {
                for (const m of members) {
                  this.senderCache.set(m.member_id, {
                    name: m.member_name,
                    imageUrl: m.member_image_url || '',
                    isAdmin: m.member_role === 'administrator',
                  });
                }
                sender = this.senderCache.get(payload.new.sender_id);
              }
            }

            const message: ChatMessage = {
              id: payload.new.id,
              text: payload.new.text,
              timestamp: new Date(payload.new.timestamp),
              senderId: payload.new.sender_id,
              senderName: sender?.name || 'Unknown',
              senderAvatar: sender?.imageUrl || '',
              siteOfficeId: payload.new.site_office_id,
              isRead: payload.new.is_read,
              isAdmin: sender?.isAdmin || false,
              type: payload.new.type || 'text',
            };

            onMessage(message);
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            onError?.('Failed to subscribe to chat updates');
          }
        });

      this.subscriptions.set(channelName, channel);

      return () => {
        supabase.removeChannel(channel);
        this.subscriptions.delete(channelName);
      };
    } catch (error) {
      console.error('Error subscribing to messages:', error);
      onError?.(error);
      return () => {};
    }
  }

  // Unsubscribe from all message updates
  static unsubscribeAll(): void {
    try {
      this.subscriptions.forEach((channel) => {
        supabase.removeChannel(channel);
      });
      this.subscriptions.clear();
      console.log('Unsubscribed from all chat channels');
    } catch (error) {
      console.error('Error unsubscribing from channels:', error);
    }
  }

  // Get unread message count for a user
  static async getUnreadCount(userId: string, siteOfficeId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('site_office_id', siteOfficeId)
        .eq('is_read', false)
        .neq('sender_id', userId);

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Search messages
  static async searchMessages(
    siteOfficeId: string,
    query: string,
    limit: number = 20
  ): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, text, timestamp, sender_id, site_office_id, is_read, type')
        .eq('site_office_id', siteOfficeId)
        .ilike('text', `%${query}%`)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error searching messages:', error);
        return [];
      }

      return (data || []).map((msg: any) => {
        const sender = this.senderCache.get(msg.sender_id);
        return {
          id: msg.id,
          text: msg.text,
          timestamp: new Date(msg.timestamp),
          senderId: msg.sender_id,
          senderName: sender?.name || 'Unknown',
          senderAvatar: sender?.imageUrl || '',
          siteOfficeId: msg.site_office_id,
          isRead: msg.is_read,
          isAdmin: sender?.isAdmin || false,
          type: msg.type || 'text',
        };
      });
    } catch (error) {
      console.error('Error searching messages:', error);
      return [];
    }
  }

  // Delete a message
  static async deleteMessage(messageId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', messageId);

      if (error) {
        console.error('Error deleting message:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      return false;
    }
  }

  // Get message history with pagination
  static async getMessageHistory(
    siteOfficeId: string,
    offset: number = 0,
    limit: number = 50
  ): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, text, timestamp, sender_id, site_office_id, is_read, type')
        .eq('site_office_id', siteOfficeId)
        .order('timestamp', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error getting message history:', error);
        return [];
      }

      return (data || []).map((msg: any) => {
        const sender = this.senderCache.get(msg.sender_id);
        return {
          id: msg.id,
          text: msg.text,
          timestamp: new Date(msg.timestamp),
          senderId: msg.sender_id,
          senderName: sender?.name || 'Unknown',
          senderAvatar: sender?.imageUrl || '',
          siteOfficeId: msg.site_office_id,
          isRead: msg.is_read,
          isAdmin: sender?.isAdmin || false,
          type: msg.type || 'text',
        };
      }).reverse();
    } catch (error) {
      console.error('Error getting message history:', error);
      return [];
    }
  }

  // Archive old messages (admin only)
  static async archiveOldMessages(
    siteOfficeId: string,
    olderThanDays: number = 30
  ): Promise<number> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const { data, error } = await supabase
        .from('chat_messages')
        .update({ archived: true })
        .eq('site_office_id', siteOfficeId)
        .lt('timestamp', cutoffDate.toISOString())
        .select();

      if (error) {
        console.error('Error archiving messages:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error archiving messages:', error);
      return 0;
    }
  }

  // Get chat settings for a site office
  static async getChatSettings(siteOfficeId: string) {
    try {
      const { data, error } = await supabase
        .from('chat_settings')
        .select('*')
        .eq('site_office_id', siteOfficeId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error getting chat settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting chat settings:', error);
      return null;
    }
  }

  // Update chat settings (admin only)
  static async updateChatSettings(
    siteOfficeId: string,
    settings: {
      auto_delete?: boolean;
      auto_delete_period?: number;
      notifications?: boolean;
      sound?: boolean;
    }
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('chat_settings')
        .upsert({
          site_office_id: siteOfficeId,
          ...settings,
        }, {
          onConflict: 'site_office_id',
        });

      if (error) {
        console.error('Error updating chat settings:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating chat settings:', error);
      return false;
    }
  }

  // Mark all messages as read for a site office
  static async markMessagesAsRead(siteOfficeId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .eq('site_office_id', siteOfficeId)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking messages as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return false;
    }
  }

  static async getSettings(siteOfficeId: string) {
    return this.getChatSettings(siteOfficeId);
  }

  static async saveSettings(siteOfficeId: string, settings: any): Promise<boolean> {
    return this.updateChatSettings(siteOfficeId, settings);
  }

  static async runAutoDelete(siteOfficeId: string): Promise<number> {
    const settings = await this.getChatSettings(siteOfficeId);
    if (!settings?.auto_delete) return 0;
    
    // Actually DELETE old messages (not just archive)
    return this.deleteOldMessages(siteOfficeId, settings.auto_delete_period);
  }

  // Delete old messages permanently
  static async deleteOldMessages(
    siteOfficeId: string,
    olderThanDays: number = 30
  ): Promise<number> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const { data, error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('site_office_id', siteOfficeId)
        .lt('timestamp', cutoffDate.toISOString())
        .select();

      if (error) {
        console.error('Error deleting old messages:', error);
        return 0;
      }

      console.log(`✅ Auto-deleted ${data?.length || 0} messages older than ${olderThanDays} days`);
      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting old messages:', error);
      return 0;
    }
  }

  // Delete multiple messages by IDs
  static async deleteMessages(messageIds: string[]): Promise<number> {
    if (messageIds.length === 0) return 0;
    
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .delete()
        .in('id', messageIds)
        .select();

      if (error) {
        console.error('Error deleting messages:', error);
        throw error;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting messages:', error);
      throw error;
    }
  }

  static async archiveMessage(messageId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ archived: true })
        .eq('id', messageId);

      if (error) {
        console.error('Error archiving message:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error archiving message:', error);
      return false;
    }
  }

  static async deleteMessagesBefore(siteOfficeId: string, date: Date): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('site_office_id', siteOfficeId)
        .lt('timestamp', date.toISOString())
        .select();

      if (error) {
        console.error('Error deleting messages:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting messages:', error);
      return 0;
    }
  }

  static async archiveMessagesBefore(siteOfficeId: string, date: Date): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .update({ archived: true })
        .eq('site_office_id', siteOfficeId)
        .lt('timestamp', date.toISOString())
        .select();

      if (error) {
        console.error('Error archiving messages:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error archiving messages:', error);
      return 0;
    }
  }

  static async deleteAllMessages(siteOfficeId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('site_office_id', siteOfficeId)
        .select();

      if (error) {
        console.error('Error deleting all messages:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting all messages:', error);
      return 0;
    }
  }

  static async checkAndRunAutoDelete(siteOfficeId: string): Promise<number> {
    return this.runAutoDelete(siteOfficeId);
  }
}

export default ChatService;
